import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowDown, ArrowUpRight, Sparkles } from "lucide-react";
import { person, traits } from "../lib/data";

// Big black sun SVG w/ radiating rays
const BlackSun = ({ size = 560 }) => (
  <svg width={size} height={size} viewBox="0 0 200 200" className="text-[color:var(--nn-accent)]">
    <defs>
      <radialGradient id="core" cx="50%" cy="50%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="1" />
        <stop offset="60%" stopColor="currentColor" stopOpacity="0.9" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
      </radialGradient>
    </defs>
    <g fill="currentColor">
      {Array.from({ length: 32 }).map((_, i) => {
        const angle = (i / 32) * 360;
        return (
          <rect
            key={i}
            x={99}
            y={4}
            width={2}
            height={44}
            rx={1}
            transform={`rotate(${angle} 100 100)`}
            opacity={0.75}
          />
        );
      })}
      <circle cx="100" cy="100" r="42" fill="url(#core)" />
      <circle cx="100" cy="100" r="42" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.3" />
    </g>
  </svg>
);

const Sticker = ({ children, initial, testid, className = "", drag = true, style = {} }) => (
  <motion.div
    drag={drag}
    dragMomentum={false}
    dragElastic={0.15}
    whileDrag={{ scale: 1.05, rotate: 2 }}
    whileHover={{ scale: 1.02 }}
    initial={{ opacity: 0, y: 30, scale: 0.9 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    className={`sticker ${className}`}
    style={{ ...initial, ...style }}
    data-cursor="dragging"
    data-cursor-label="drag me"
    data-testid={testid}
  >
    {children}
  </motion.div>
);

const Hero = () => {
  const ref = useRef(null);
  const [time, setTime] = useState("");
  const [now, setNow] = useState(new Date());

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const sunY = useTransform(scrollYProgress, [0, 1], [0, 220]);

  // Mouse tracking for parallax sun
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, { stiffness: 40, damping: 20 });
  const sy = useSpring(my, { stiffness: 40, damping: 20 });
  const sunTx = useTransform(sx, [0, 1], [-30, 30]);
  const sunTy = useTransform(sy, [0, 1], [-20, 20]);

  useEffect(() => {
    const tick = () => {
      const d = new Date();
      setNow(d);
      setTime(
        d.toLocaleTimeString("en-US", { timeZone: "America/Toronto", hour12: false })
      );
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const handleMouse = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width);
    my.set((e.clientY - rect.top) / rect.height);
  };

  return (
    <section
      ref={ref}
      onMouseMove={handleMouse}
      className="relative min-h-[100svh] flex flex-col overflow-hidden pt-24 md:pt-28"
      data-testid="hero"
    >
      {/* TOP TICKER */}
      <div className="border-y border-[color:var(--nn-border)] py-3 md:py-4 overflow-hidden bg-[color:var(--nn-bg)]">
        <Marquee gradient={false} speed={55}>
          <div className="flex items-center gap-10 mx-6 hero-ticker text-lg md:text-2xl">
            <span className="text-[color:var(--nn-accent)]">◆</span>
            <span>Available for full-time & selected projects</span>
            <span className="text-[color:var(--nn-accent)]">◆</span>
            <span className="font-serif-i text-[color:var(--nn-muted)]">Booking Q1 / Q2 2026</span>
            <span className="text-[color:var(--nn-accent)]">◆</span>
            <span>Product · UI/UX · Motion</span>
            <span className="text-[color:var(--nn-accent)]">◆</span>
            <span className="font-serif-i text-[color:var(--nn-muted)]">Remote first — Toronto, CA</span>
            <span className="text-[color:var(--nn-accent)]">◆</span>
          </div>
        </Marquee>
      </div>

      {/* MAIN STAGE */}
      <div className="flex-1 relative">
        {/* Rotating sun (center-back) */}
        <motion.div
          style={{ y: sunY, x: sunTx, translateY: sunTy }}
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          aria-hidden
        >
          <div className="nn-spin-slow">
            <BlackSun size={640} />
          </div>
        </motion.div>

        {/* Content grid */}
        <div className="relative z-10 mx-auto max-w-[1600px] px-5 md:px-10 h-full grid grid-cols-12 gap-4 pt-10 md:pt-16">
          {/* Left meta rail */}
          <aside className="col-span-6 md:col-span-3 flex flex-col justify-between text-[10px] font-mono uppercase tracking-widest text-[color:var(--nn-muted)]">
            <div className="space-y-1">
              <div>{now.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "2-digit" })} · Vol. 03</div>
              <div className="text-[color:var(--nn-text)]" data-testid="hero-clock">Toronto — {time || "—"}</div>
              <div className="flex items-center gap-2 mt-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--nn-accent)] animate-pulse" />
                <span className="text-[color:var(--nn-accent-text)]">Available for hire</span>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="hidden md:block leading-relaxed text-[color:var(--nn-text)]/80 normal-case font-sans text-[13px] tracking-normal max-w-[220px]"
            >
              I design intentional digital products with 5+ years of craft, judgment, and a bias for shipping.
            </motion.div>
          </aside>

          {/* Center — sun + sticker cluster */}
          <div className="col-span-6 md:col-span-6 relative min-h-[420px] md:min-h-[520px]">
            <div className="absolute inset-0">
              <Sticker
                initial={{ top: "6%", left: "14%", rotate: -6 }}
                className="max-w-[220px]"
                testid="sticker-role"
              >
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] mb-1">
                  → role
                </div>
                <div className="font-display text-lg leading-tight">Senior Product & UI/UX Designer</div>
              </Sticker>

              <Sticker
                initial={{ top: "56%", left: "6%", rotate: 4 }}
                className="!p-0 !bg-transparent !border-0 !shadow-none"
                testid="sticker-portrait"
              >
                <img
                  src={person.portrait}
                  alt="Nirav Nayak"
                  draggable={false}
                  className="w-28 md:w-40 aspect-[3/4] object-cover rounded-xl border border-[color:var(--nn-border)] shadow-[0_20px_40px_-20px_rgba(0,0,0,0.6)]"
                />
              </Sticker>

              <Sticker
                initial={{ top: "18%", right: "6%", rotate: 6 }}
                className="!bg-[color:var(--nn-accent)] !text-[color:var(--nn-accent-ink)] !border-[color:var(--nn-accent)]"
                testid="sticker-current"
              >
                <div className="font-mono text-[10px] uppercase tracking-widest mb-1">Currently</div>
                <div className="font-display text-base font-semibold">YuJa Inc — Toronto</div>
              </Sticker>

              <Sticker
                initial={{ bottom: "16%", right: "10%", rotate: -3 }}
                className="!py-2"
                testid="sticker-tag"
              >
                <div className="font-serif-i text-lg">"just ship it"</div>
              </Sticker>
            </div>
          </div>

          {/* Right rail — traits */}
          <aside className="hidden md:flex md:col-span-3 flex-col items-end justify-between text-right pb-8 md:pb-12">
            <div className="flex flex-col items-end gap-2">
              {traits.map((t) => (
                <span key={t} className="chip" data-testid={`trait-${t.toLowerCase().replace(/\s+/g, "-")}`}>
                  <span className="w-1 h-1 rounded-full bg-[color:var(--nn-accent)]" />
                  {t}
                </span>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.7 }}
              className="flex flex-col items-end gap-3 mt-8"
            >
              <a
                href="#work"
                onClick={(e) => { e.preventDefault(); document.querySelector("#work")?.scrollIntoView({ behavior: "smooth" }); }}
                data-cursor="dragging"
                data-cursor-label="view"
                data-testid="hero-cta"
                className="group inline-flex items-center gap-2 pl-5 pr-2 py-2 rounded-full bg-[color:var(--nn-accent)] text-[color:var(--nn-accent-ink)] font-medium hover:gap-3 transition-all"
              >
                Selected work
                <span className="w-9 h-9 rounded-full grid place-items-center bg-[color:var(--nn-accent-ink)] text-[color:var(--nn-accent)]">
                  <ArrowDown size={16} />
                </span>
              </a>
              <a
                href="#contact"
                onClick={(e) => { e.preventDefault(); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }}
                data-cursor="hover"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[color:var(--nn-border)] hover:border-[color:var(--nn-accent)] transition-colors"
              >
                Get in touch <ArrowUpRight size={14} />
              </a>
            </motion.div>
          </aside>
        </div>
      </div>

      {/* GIANT BOTTOM NAME */}
      <div className="relative overflow-hidden border-t border-[color:var(--nn-border)] py-6 md:py-10 mask-fade-x">
        <Marquee gradient={false} speed={45} pauseOnHover>
          <div className="giant-text flex items-center gap-8 md:gap-16 text-[14vw] md:text-[11vw] leading-[0.88] pr-8 md:pr-16">
            <span>NIRAV NAYAK</span>
            <Sparkles className="text-[color:var(--nn-accent)] shrink-0" size={64} />
            <span className="text-outline">PRODUCT DESIGNER</span>
            <Sparkles className="text-[color:var(--nn-accent)] shrink-0" size={64} />
          </div>
        </Marquee>
      </div>
    </section>
  );
};

export default Hero;
